import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TimeHelper from '../../../utils/time';

interface DailyTrend {
    date: string;
    productionCount: number;
    successfulCount: number;
    failedCount: number;
}

interface ProductionTrendChartProps {
    data: DailyTrend[];
}

export const ProductionTrendChart: FC<ProductionTrendChartProps> = ({ data }) => {
    const { t } = useTranslation();

    // Veri kontrolü
    if (!data || data.length === 0) {
        return (
            <Card elevation={3} sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('aiStudioAnalytics.productionTrend')}
                    </Typography>
                    <Box sx={{ width: '100%', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="textSecondary">{t('aiStudioAnalytics.noData')}</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Tarih formatla (Recharts için)
    const formatTrendDate = (date: string): string => {
        if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
            const [year, month, day] = date.substring(0, 10).split('-');
            return `${day}.${month}.${year}`;
        }
        return TimeHelper.formatDateWithDot(date);
    };

    const formattedData = data.map((item) => ({
        date: formatTrendDate(item.date),
        productionCount: item.productionCount,
        successfulCount: item.successfulCount,
        failedCount: item.failedCount,
    }));

    return (
        <Card elevation={0} sx={{ mb: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: '24px !important' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                    {t('aiStudioAnalytics.productionTrend')}
                </Typography>
                <Box sx={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer>
                        <LineChart data={formattedData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis 
                                dataKey="date" 
                                tickLine={false} 
                                axisLine={false} 
                                dy={10} 
                                style={{ fontSize: '0.75rem', fill: '#64748b', fontWeight: 500 }} 
                            />
                            <YAxis 
                                tickLine={false} 
                                axisLine={false} 
                                dx={-5} 
                                style={{ fontSize: '0.75rem', fill: '#64748b', fontWeight: 500 }} 
                            />
                            <Tooltip 
                                contentStyle={{ 
                                    backgroundColor: '#ffffff', 
                                    border: '1px solid #e2e8f0', 
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    fontSize: '0.8125rem'
                                }} 
                            />
                            <Legend 
                                verticalAlign="top" 
                                height={36} 
                                iconType="circle"
                                iconSize={8}
                                wrapperStyle={{ fontSize: '0.8125rem', fontWeight: 500, color: '#64748b' }}
                            />
                            <Line
                                type="monotone"
                                dataKey="productionCount"
                                stroke="#1976d2"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 6 }}
                                name={t('aiStudioAnalytics.productionCount')}
                            />
                            <Line
                                type="monotone"
                                dataKey="successfulCount"
                                stroke="#10b981"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 6 }}
                                name={t('aiStudioAnalytics.successful')}
                            />
                            <Line
                                type="monotone"
                                dataKey="failedCount"
                                stroke="#ef4444"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 6 }}
                                name={t('aiStudioAnalytics.failed')}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
};

